import { publicProfileKeys } from '@/features/profile/usePublicProfile';
import { useAuth } from '@/lib/auth/auth';
import { useWritableMarketMutation } from '@/features/market/useWritableMarketMutation';
import { type PublicProfileResult } from '@/lib/api/apiTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function usePublicProfileMutations() {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  const toggleFollow = useWritableMarketMutation(useMutation({
    mutationFn: async ({
      username,
      isFollowing,
    }: {
      username: string;
      isFollowing: boolean;
    }) => {
      if (isFollowing) {
        await request(`/profiles/${username}/follow`, { method: 'DELETE' });
        return false;
      }

      await request(`/profiles/${username}/follow`, { method: 'POST' });
      return true;
    },
    onMutate: async ({ username, isFollowing }) => {
      await queryClient.cancelQueries({ queryKey: publicProfileKeys.detail(username) });
      const snapshot = queryClient.getQueryData<PublicProfileResult>(publicProfileKeys.detail(username));

      queryClient.setQueryData<PublicProfileResult>(publicProfileKeys.detail(username), (current) =>
        current
          ? {
              ...current,
              relationship: {
                ...current.relationship,
                is_following: !isFollowing,
              },
              stats: {
                ...current.stats,
                follower_count: Math.max(0, current.stats.follower_count + (isFollowing ? -1 : 1)),
              },
            }
          : current
      );

      return { snapshot };
    },
    onError: (_error, variables, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(publicProfileKeys.detail(variables.username), context.snapshot);
      }
    },
    onSettled: async () => {},
  }));

  const toggleBlock = useWritableMarketMutation(useMutation({
    mutationFn: async ({
      username,
      isBlocked,
    }: {
      username: string;
      isBlocked: boolean;
    }) => {
      if (isBlocked) {
        await request(`/profiles/${username}/block`, { method: 'DELETE' });
        return false;
      }
      await request(`/profiles/${username}/block`, { method: 'POST' });
      return true;
    },
    onMutate: async ({ username, isBlocked }) => {
      await queryClient.cancelQueries({ queryKey: publicProfileKeys.detail(username) });
      const snapshot = queryClient.getQueryData<PublicProfileResult>(publicProfileKeys.detail(username));

      queryClient.setQueryData<PublicProfileResult>(publicProfileKeys.detail(username), (current) =>
        current
          ? {
              ...current,
              relationship: {
                ...current.relationship,
                is_blocked_by_me: !isBlocked,
              },
            }
          : current
      );

      return { snapshot };
    },
    onError: (_error, variables, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(publicProfileKeys.detail(variables.username), context.snapshot);
      }
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: publicProfileKeys.detail(variables.username) });
    },
  }));

  return {
    toggleFollow,
    toggleBlock,
  };
}
